import Vue from 'vue';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import { Themes } from '@pendo/services/BuildingBlocks';
import { AppsService } from '@/common/apps/apps.service';
import { fetchThemes, fetchOrganizationThemes, createTheme, deleteTheme } from './themes.utils';
import { computeGuidePlatformType } from '@/utils/apps';
import { canCreateGuide } from '@/guides/helpers/common.util';

export const themes = {
    namespaced: true,
    state: {
        map: {},
        areAllSubThemesLoaded: false,
    },
    mutations: {
        setThemes (state, { themes }) {
            state.map = keyBy(themes, 'id');
        },
        setTheme (state, { theme }) {
            Vue.set(state.map, theme.id, theme);
        },
        removeThemeFromMap (state, { id }) {
            Vue.delete(state.map, id);
        },
        setAreAllSubThemesLoaded (state, { loaded }) {
            state.areAllSubThemesLoaded = loaded;
        },
    },
    actions: {
        async loadAll ({ rootGetters, state, commit }, { reFetch = false } = {}) {
            if (Object.keys(state.map).length && !reFetch) {
                return;
            }

            const activeOrganization = rootGetters['organization/getActive'];
            const orgId = get(activeOrganization, 'id', null);
            const activeSubscription = rootGetters['subscription/getActiveSub'];
            const subId = get(activeSubscription, 'id', null);
            const includeOrganizationResources = get(
                activeOrganization,
                'organizationFlags.enableResourceSharing',
                false,
            );

            // this will get removed in APP-22605 when backend implements an "all" query param
            const apps = rootGetters['apps/list'];
            const appThemeRequests = [];
            const orgThemeRequests = [];

            if (orgId && includeOrganizationResources) {
                orgThemeRequests.push(fetchOrganizationThemes(orgId, subId));
            }

            apps.filter((app) => app.integrated || AppsService.isLegacyApp(app))
                .forEach(({ platform, id }) => {
                    const guidePlatformType = computeGuidePlatformType(platform);
                    const canCreate = canCreateGuide(id);

                    appThemeRequests.push(
                        fetchThemes(id).then(async (themes) => {
                            let defaultTheme = themes.find((theme) => theme.tags.toLowerCase().includes('default'));

                            if (!defaultTheme && canCreate) {
                                defaultTheme = Themes.createTheme({
                                    name: 'Default',
                                    type: 'default',
                                    platform: guidePlatformType,
                                });
                                defaultTheme.appId = id;
                                const theme = await createTheme(defaultTheme);
                                themes.push(theme);
                            }

                            return themes;
                        }),
                    );
                });
            const allThemes = await Promise.all(appThemeRequests.concat(orgThemeRequests));
            // eslint-disable-next-line prefer-spread
            commit('setThemes', { themes: [].concat.apply([], allThemes) });
            commit('setAreAllSubThemesLoaded', { loaded: true });
        },
        async createTheme ({ commit }, { theme }) {
            const savedTheme = await createTheme(theme);
            commit('setTheme', { theme: savedTheme });

            return savedTheme.id;
        },
        async deleteTheme ({ commit }, { id }) {
            await deleteTheme(id);
            commit('removeThemeFromMap', { id });
        },
    },
    getters: {
        getDefaultThemeForAppId: (state, getters) => (appId = -323232) => {
            const appThemes = getters.listForAppId(appId);

            return appThemes.find((theme) => theme.tags.toLowerCase().includes('default'));
        },
        getThemeById: (state) => (id) => state.map[id],
        list: (state) => values(state.map),
        listForAppId: (store, getters) => (appId) => {
            return getters.list.filter((theme) => theme.appId === appId);
        },
        getOrgThemesForAppId: (state, getters, rootState, rootGetters) => (appId) => {
            const app = rootGetters['apps/getAppById'](appId);

            if (!app) return [];

            const subscription = rootGetters['subscription/getActiveSub'];

            if (!subscription) return [];

            const activeSubId = get(subscription, 'id');
            const appPlatform = computeGuidePlatformType(app.platform);

            return getters.getOrgThemes.filter((theme) => {
                const { orgSharedResourceSettings } = theme || {};
                const { platform, subList = [] } = orgSharedResourceSettings || {};

                return appPlatform === platform && subList.map(String).includes(activeSubId);
            });
        },
        getOrgThemes: (state, getters) => {
            return getters.list.filter((theme) => theme.orgSharedResourceSettings);
        },
    },
};

export default themes;
